/**
 * ExpiryDeadlineCell — shared "Wygasa" column cell for the `/reservations`
 * flat list and the media-unit Rezerwacje tab (reservation-panel spec:
 * "Reservation expiry deadline is visible on every reservation list
 * surface").
 *
 * Renders one of four distinct states, per the spec:
 * - 'never'     — Stałka/Sprzedana: explicit never-expires marker
 * - 'none'      — Wstępna/Potwierdzona with no deadline set: a distinct
 *                 "no deadline set" marker (an operational gap — must NOT
 *                 read like 'never', a legitimate terminal state)
 * - 'scheduled' — the formatted effective-expiry date
 * - 'expired'   — the elapsed date plus an explicit expired-and-awaiting-
 *                 release marker (the most urgent row on the screen)
 */
import { resolveDeadlineDisplay } from '../lib/formatters';

export interface ExpiryDeadlineCellLabels {
  neverExpiresLabel: string;
  noDeadlineSetLabel: string;
  expiredMarkerLabel: string;
}

export interface ExpiryDeadlineCellProps {
  status: 'SOFT_RESERVATION' | 'HARD_RESERVATION' | 'SOLD' | 'OPTION';
  effectiveExpiresAt: string | null;
  labels: ExpiryDeadlineCellLabels;
  /** Unique per-row test id, e.g. `reservation-deadline-${item.id}`. */
  testId: string;
}

export function ExpiryDeadlineCell({
  status,
  effectiveExpiresAt,
  labels,
  testId,
}: ExpiryDeadlineCellProps) {
  const display = resolveDeadlineDisplay(status, effectiveExpiresAt);

  if (display.kind === 'never') {
    return (
      <span data-testid={testId} className="text-sm italic text-muted-foreground">
        {labels.neverExpiresLabel}
      </span>
    );
  }

  if (display.kind === 'none') {
    return (
      <span data-testid={testId} className="text-sm font-medium text-warning">
        {labels.noDeadlineSetLabel}
      </span>
    );
  }

  if (display.kind === 'expired') {
    return (
      <div data-testid={testId}>
        <p className="text-sm font-medium text-destructive">{display.formattedDate}</p>
        <p className="text-xs text-destructive">{labels.expiredMarkerLabel}</p>
      </div>
    );
  }

  return (
    <span data-testid={testId} className="text-sm">
      {display.formattedDate}
    </span>
  );
}
